import React from 'react';
import { SegmentedControl } from '../ui/SegmentedControl';
import { cn } from '../../lib/utils';

export type PurchasingModuleFilterValue = 'all' | 'supplies' | 'spares';

export type PurchasingModuleFilterProps = {
  value: PurchasingModuleFilterValue;
  onChange: (value: PurchasingModuleFilterValue) => void;
  /** Counts shown as badges on each option. */
  counts?: Partial<Record<PurchasingModuleFilterValue, number>>;
  showAll?: boolean;
  showSupplies?: boolean;
  showSpares?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  'aria-label'?: string;
};

/**
 * Segmented All / Supplies / Spares control for combined Purchasing views.
 */
export const PurchasingModuleFilter: React.FC<PurchasingModuleFilterProps> = ({
  value,
  onChange,
  counts,
  showAll = true,
  showSupplies = true,
  showSpares = true,
  className,
  size = 'sm',
  'aria-label': ariaLabel = 'Filter by module',
}) => {
  const options: Array<{
    id: PurchasingModuleFilterValue;
    label: string;
    show: boolean;
  }> = [
    { id: 'all', label: 'All', show: showAll },
    { id: 'supplies', label: 'Supplies', show: showSupplies },
    { id: 'spares', label: 'Spares', show: showSpares },
  ];

  const visible = options
    .filter((o) => o.show)
    .map((o) => ({
      id: o.id,
      label: o.label,
      count: counts?.[o.id],
    }));

  if (visible.length <= 1) return null;

  return (
    <SegmentedControl
      className={cn('shadow-sm', className)}
      size={size}
      aria-label={ariaLabel}
      value={value}
      onChange={onChange}
      options={visible}
      hideZeroCounts
    />
  );
};

export default PurchasingModuleFilter;
